// Where an eBay reply goes (2026-10-27, Jenn's spec "Agent drafts on eBay
// cases, inquiries and returns"; docs/contact-view.md, "The reply's
// destination"). On the agent draft layout (A2), the reply box on an eBay
// contact whose order has an open eBay inquiry or return IS the message to
// the buyer in that case: CXHub posts it to the case
// (server/src/services/ebay-reply-destination.js), and there is no separate
// "respond in the case" step with its own message box.
//
//   inquiry: post-order sendInquiryMessage(inquiryId, text)
//   return:  post-order sendReturnMessage(returnId, text)
//   case:    an escalated eBay case (casemanagement). eBay's Post-Order API
//            has no message call for a case, so the reply goes as a member
//            message on the order thread, and the destination line says so.
//   member:  no open case: a regular eBay member message, as before.
//
// Pure: the server builds the destination at generation
// (modelMeta.replyDestination) and the send path and both panels read it.

// eBay Post-Order InquiryStatusEnum's closed values (ebay-case-lookup.js's
// CLOSED_INQUIRY_STATUSES; the same three).
const CLOSED_INQUIRY = new Set(['CLOSED', 'CLOSED_WITH_ESCALATION', 'CS_CLOSED'])

export function isClosedInquiryStatus(status) {
  return CLOSED_INQUIRY.has(String(status ?? '').trim().toUpperCase())
}

// A return is open unless eBay's status says closed (CLOSED, RETURN_CLOSED,
// CS_CLOSED and the like). A missing status is open (the search found it).
export function isClosedReturnStatus(status) {
  return /CLOSED/i.test(String(status ?? ''))
}

// A case (casemanagement) is open unless its status says closed.
export function isClosedCaseStatus(status) {
  return /CLOSED/i.test(String(status ?? ''))
}

// The destination for a contact: { kind, id, status, noReply, reason }.
//   channel: the conversation's channel (only 'ebay' has a case
//     destination; every other channel is null: the reply goes as today).
//   caseContext: shadow-inputs.js's buildCaseContext().context ({ kind
//     'INR inquiry' | 'eBay inquiry' | 'eBay case', id, status,
//     waitingForBuyer, buyerSpokeSinceOurLast }) or null.
//   ebayReturn: the order's eBay return (enrichment.summary.ebayReturnFound,
//     { id, status }) or null.
// An open inquiry wins, then an open return, then an open escalated case.
// noReply: the case is waiting for the buyer and the buyer has not written
// since our last message (the rule for waiting on the buyer stays).
export function replyDestinationFor({ channel = null, caseContext = null, ebayReturn = null } = {}) {
  if (channel !== 'ebay') return null
  const c = caseContext?.id ? caseContext : null
  const waiting = Boolean(c?.waitingForBuyer) && c?.buyerSpokeSinceOurLast !== true
  if (c && /inquiry/i.test(String(c.kind ?? '')) && !isClosedInquiryStatus(c.status)) {
    return { kind: 'inquiry', id: String(c.id), status: c.status ?? null, noReply: waiting }
  }
  if (ebayReturn?.id && !isClosedReturnStatus(ebayReturn.status)) {
    return { kind: 'return', id: String(ebayReturn.id), status: ebayReturn.status ?? null, noReply: /WAITING_BUYER|WAITING_FOR_BUYER/i.test(String(ebayReturn.status ?? '')) }
  }
  if (c && /case/i.test(String(c.kind ?? '')) && !isClosedCaseStatus(c.status)) {
    return { kind: 'case', id: String(c.id), status: c.status ?? null, noReply: waiting }
  }
  return { kind: 'member', id: null, status: null, noReply: false }
}

// Whether the reply is posted to the case itself (an inquiry or a return).
export function postsToCase(dest) {
  return Boolean(dest?.id) && (dest.kind === 'inquiry' || dest.kind === 'return')
}

const CASE_NAMES = { inquiry: 'eBay inquiry', return: 'eBay return', case: 'eBay case' }
export function caseName(dest) {
  return dest?.id && CASE_NAMES[dest.kind] ? `${CASE_NAMES[dest.kind]} ${dest.id}` : null
}

// The line under the reply box's To line (admin- and CSR-visible):
//   "Sends to eBay inquiry 5123456789"
//   "Sends to eBay return 5098765432"
//   "Sends as an eBay member message: eBay has no message call for eBay case 5011111111"
//   "Sends to eBay member message"
//   "No reply: the inquiry is waiting for the buyer" (hasReply false and
//     the case waits for the buyer)
// hasReply: whether there is a reply to send (the waiting line shows only
// without one; a person who writes a reply anyway sends it to the case).
export function replyDestinationLine(dest, { hasReply = true } = {}) {
  if (!dest) return null
  if (!hasReply && dest.noReply && dest.kind !== 'member') {
    return `No reply: the ${dest.kind === 'case' ? 'case' : dest.kind} is waiting for the buyer`
  }
  if (postsToCase(dest)) return `Sends to ${caseName(dest)}`
  if (dest.kind === 'case' && dest.id) return `Sends as an eBay member message: eBay has no message call for ${caseName(dest)}`
  return 'Sends to eBay member message'
}

// The Activity Log's words for the send (send-approved-reply.js).
export function replyPostedNote(dest) {
  return postsToCase(dest) ? `Reply posted to ${caseName(dest)}` : 'Reply sent as an eBay member message'
}
export function replyFallbackNote(dest, why = 'closed') {
  return `${caseName(dest) ?? 'The eBay case'} was ${why} at Send: the reply was sent as an eBay member message instead`
}
export function caseNoMessageCallNote(dest) {
  return `eBay has no message call for ${caseName(dest) ?? 'this eBay case'}: the reply was sent as an eBay member message`
}

// The steps that only message the buyer in the case: on the A2 layout with
// the reply posted to the case they are left out (the reply is the case
// message, sent once). The manual inquiry reply (inr_request_id_process with
// no disposition), the inquiry message (inr_send_message) and a task's case
// message.
export function isCaseMessageStep(a) {
  if (!a?.type) return false
  if (a.type === 'inr_send_message' || a.type === 'task_case_message') return true
  return a.type === 'inr_request_id_process' && !a.params?.disposition
}

// The case actions that carry their own buyer-facing comment field in
// eBay's API (action-executor.js reads rawInput.editedMessage for both): the
// inquiry refund's comments and the shipment proof's sellerComments. On the
// A2 layout with the reply posted to that inquiry, the comment is the reply,
// and the reply is not sent again.
export const CASE_COMMENT_STEP_TYPES = Object.freeze(['inr_issue_refund', 'inr_provide_shipment_info'])

// The index of the step that carries the reply as its comment, or -1: the
// first comment step on the destination's inquiry that is checked (checked:
// a Set or an array of indexes; null means every step) and not done yet
// (done: indexes already processed).
export function replyCarrierStepIndex(actions = [], dest = null, { checked = null, done = [] } = {}) {
  if (dest?.kind !== 'inquiry' || !dest.id) return -1
  const isOn = (i) => (checked == null ? true : (checked instanceof Set ? checked.has(i) : checked.includes(i)))
  return (actions ?? []).findIndex((a, i) => CASE_COMMENT_STEP_TYPES.includes(a?.type)
    && String(a?.params?.ebayCase ?? '') === String(dest.id) && isOn(i) && !(done ?? []).includes(i))
}

// --- eBay refuses a reply posted to a return (2026-11-04, docs/decisions/
// 2026-11-04-ebay-return-reply-refused.md; docs/contact-view.md "The reply's
// destination") ---------------------------------------------------------------
//
// A return in a state that takes no messages (RETURN_NO_MESSAGE_STATES
// below: ITEM_READY_TO_SHIP, READY_FOR_SHIPPING, ITEM_SHIPPED) answers send_message with error 1502
// "Illegal State transition" (HTTP 403), or a 403 whose message says the
// activity is not allowed in this state. The reply is then NOT sent: the
// contact stays open with its owner, and the panel offers one button that
// sends the same reply to the buyer as an eBay message.

export const RETURN_REPLY_REFUSED_STATE = 'RETURN_REPLY_REFUSED_STATE'

// eBay Post-Order error ids that mean "this return takes no message at this
// stage". Only codes seen from eBay go here (1502 in prod, 10/28-11/03).
export const RETURN_REPLY_STATE_CODES = Object.freeze({
  1502: 'Illegal State transition',
})

// The return states (eBay's state or status) that take no messages, in ONE
// place (2026-11-04, docs/decisions/2026-11-04-ebay-return-reply-message-
// first.md). Only states eBay has refused a return message at go here:
// ITEM_READY_TO_SHIP / READY_FOR_SHIPPING (10/28-11/03, "state:itemReady
// ToShip" in the 1502) and ITEM_SHIPPED (prod, 11/04). When the read before
// the reply (getReturn) shows one of them, the reply goes straight to the
// buyer as an eBay message; any other state still posts to the return, and
// eBay's refusal (above) is the fallback. The refusal's words name the same
// state through returnStateWords().
export const RETURN_NO_MESSAGE_STATES = Object.freeze(['ITEM_READY_TO_SHIP', 'READY_FOR_SHIPPING', 'ITEM_SHIPPED'])

// A state or status as eBay's enum: itemReadyToShip / "Item ready to ship" ->
// ITEM_READY_TO_SHIP. null for none.
export function returnStateKey(state) {
  const s = String(state ?? '').trim()
  if (!s) return null
  return s.replace(/([a-z])([A-Z])/g, '$1_$2').replace(/[\s-]+/g, '_').toUpperCase()
}

// Whether a return at this state (or status) takes no messages. Unknown is
// false: the reply posts to the return as before.
export function returnTakesNoMessages(state) {
  const key = returnStateKey(state)
  return key != null && RETURN_NO_MESSAGE_STATES.includes(key)
}

// The Activity Log line when the read before the reply shows such a state
// and the reply went to the buyer as an eBay message instead.
export function returnReplyMessageFirstNote({ id, stateWords }) {
  return `eBay return ${id} is at ${stateWords ?? 'a stage that takes no messages'}, which takes no messages: sent as an eBay message to the buyer instead.`
}

// The Activity Log line when that eBay message failed: not sent, and the
// warning with its button (pendingReturnReplyRefusal()) stays.
export function returnReplyMessageFirstFailedNote({ id, stateWords }) {
  return `eBay return ${id} is at ${stateWords ?? 'a stage that takes no messages'}, which takes no messages, and the eBay message to the buyer failed. Not sent.`
}

// The thread row's label for a reply sent as an eBay message: the state
// known before the send (message first), else eBay's refusal (the button).
export function returnReplyEbayMessageThreadLabel({ stateWords = null, messageFirst = false } = {}) {
  if (messageFirst) return `Sent as an eBay message (the return is at ${stateWords ?? 'a stage that takes no messages'}, so eBay takes no messages on it)`
  return "Sent as an eBay message (eBay wouldn't post it on the return)"
}

// A 403 body that says the activity isn't allowed in the return's state.
const STATE_REFUSAL_TEXT = /illegal state|illegal activity for the state|not (?:be )?(?:allowed|permitted)[^.]{0,60}\bstate\b|\bstate\b[^.]{0,60}not (?:be )?(?:allowed|permitted)|invalid (?:return )?state/i

function ebayErrorsOf(error) {
  const body = error?.body ?? error?.errorBody ?? null
  if (Array.isArray(body?.error)) return body.error
  if (Array.isArray(body?.errors)) return body.errors
  return []
}

// 'state' | 'other'. Pure: reads the thrown error ({ status, body }) only.
export function classifyReturnReplyRefusal(error) {
  if (!error) return 'other'
  const errors = ebayErrorsOf(error)
  if (errors.some((e) => RETURN_REPLY_STATE_CODES[Number(e?.errorId)] != null)) return 'state'
  if (Number(error.status) === 403) {
    const text = [...errors.flatMap((e) => [e?.message, e?.longMessage, ...(Array.isArray(e?.parameter) ? e.parameter.map((p) => p?.value) : [])]), error?.body?.raw].filter(Boolean).join(' ')
    if (STATE_REFUSAL_TEXT.test(text)) return 'state'
  }
  return 'other'
}

// The eBay code shown with the refusal: the error id, else the HTTP status.
export function returnReplyRefusalCode(error) {
  const id = ebayErrorsOf(error).map((e) => e?.errorId).find((v) => v != null)
  if (id != null) return String(id)
  return error?.status != null ? String(error.status) : null
}

// The state eBay named in its refusal ("Illegal Activity for the
// state:itemReadyToShip; ..."), or null.
export function returnStateFromRefusal(error) {
  for (const e of ebayErrorsOf(error)) {
    for (const p of Array.isArray(e?.parameter) ? e.parameter : []) {
      const m = /state:\s*([A-Za-z_]+)/.exec(String(p?.value ?? ''))
      if (m) return m[1]
    }
  }
  return null
}

// A return state or status in words: ITEM_READY_TO_SHIP / itemReadyToShip ->
// "Item ready to ship"; READY_FOR_SHIPPING -> "Ready for shipping".
export function returnStateWords(state) {
  const s = String(state ?? '').trim()
  if (!s) return null
  const words = s.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/[_\s]+/g, ' ').trim().toLowerCase()
  return words.charAt(0).toUpperCase() + words.slice(1)
}

// The Activity Log line for the refusal.
export function returnReplyRefusedNote({ id, ebayCode, stateWords }) {
  return `Reply to eBay return ${id} refused by eBay (${ebayCode ?? 'error'}: the return is at ${stateWords ?? 'a stage that takes no messages'}). Not sent.`
}

export const SEND_AS_EBAY_MESSAGE_LABEL = 'Send this reply to the buyer as an eBay message'

// The Activity Log line for the button's send.
export function returnReplyResentNote({ by, buyerId, itemId }) {
  return `${by ?? 'A person'} chose "${SEND_AS_EBAY_MESSAGE_LABEL}". Sent to ${buyerId ?? 'the buyer'}${itemId ? ` about item ${itemId}` : ''}.`
}

// The warning's words in the panel (ReturnReplyRefusedNotice.jsx).
export const RETURN_REPLY_REFUSED_TITLE = 'Your reply was NOT sent to the customer'
// messageFirst (2026-11-04): CXHub knew the state before posting and the
// eBay message to the buyer failed; eBay was never asked, so no eBay code.
export function returnReplyRefusedReason({ kind = 'return', stateWords, ebayCode, messageFirst = false }) {
  const what = kind === 'inquiry' ? 'inquiry' : 'return'
  const base = `The ${what} is already at ${stateWords ?? 'a stage that takes no messages'}, and eBay doesn't allow new messages on a${what === 'inquiry' ? 'n' : ''} ${what} at that stage`
  if (messageFirst) return `${base}. CXHub tried to send it to the buyer as an eBay message instead, and that did not go through either.`
  return `${base} (eBay error ${ebayCode ?? 'unknown'}).`
}
export const RETURN_REPLY_REFUSED_WHAT_TO_DO = "click the blue button below. CXHub sends the same reply straight to the buyer as an eBay message. Nothing else changes, and you don't need to copy or retype anything."

// The Automation Failures entry for an automated send eBay refused.
// messageFirst (2026-11-04): the state was known before the send and the
// eBay message to the buyer failed (error: why).
export function returnReplyRefusedAutomationText({ id, ebayCode, stateWords, messageFirst = false }, error = null) {
  if (messageFirst) {
    return `eBay return ${id} is at ${stateWords ?? 'a stage that takes no messages'}, which takes no messages, and sending the reply to the buyer as an eBay message failed${error ? ` (${error})` : ''}. Not sent: the contact is back in the queue for a person, with the warning "${RETURN_REPLY_REFUSED_TITLE}" and the button "${SEND_AS_EBAY_MESSAGE_LABEL}".`
  }
  return `eBay refused the reply on eBay return ${id} (${ebayCode ?? 'error'}: the return is at ${stateWords ?? 'a stage that takes no messages'}). Not sent and not resent: the contact is back in the queue for a person, with the warning "${RETURN_REPLY_REFUSED_TITLE}" and the button "${SEND_AS_EBAY_MESSAGE_LABEL}".`
}

// The refusal still waiting for the button, from the suggestion's Activity
// Log (model_meta.apiCalls): the newest returnReplyRefused entry with no
// returnReplyResent entry after it, and only while no reply was recorded as
// sent. null otherwise.
export function pendingReturnReplyRefusal(suggestion) {
  if (!suggestion || suggestion.sentReply) return null
  const calls = suggestion.modelMeta?.apiCalls ?? []
  let found = null
  for (const c of calls) {
    if (c?.returnReplyRefused) found = c.returnReplyRefused
    else if (c?.returnReplyResent) found = null
  }
  return found
}

// The green confirmation after the button's send.
export function returnReplyResentConfirmation({ buyerId, at, stateWords }) {
  return `Sent to ${buyerId ?? 'the buyer'} at ${at}. eBay wouldn't post it on the return (${stateWords ?? 'its stage takes no messages'}), so it went as a message instead. The contact is closed.`
}
